(() => {
  if (customElements.get('td-offer-picker')) return;

  class TDOfferPicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.sectionId = this.dataset.sectionId;
      this.experimentName = this.dataset.experimentName || 'nails_offer_selector_v1';
      this.soldOutLabel = this.dataset.soldOutLabel || 'Agotado';
      this.enableMobileSticky = this.dataset.stickyEnabled === 'true';
      this.mainSection = document.getElementById(`MainProduct-${this.sectionId}`) || this.closest('[id^="MainProduct-"]');
      this.form = document.getElementById(this.dataset.formId) || this.mainSection?.querySelector('form[data-type="add-to-cart-form"]');
      this.variantInput = this.form?.querySelector('[name="id"]');
      this.productForm = this.form?.closest('product-form');
      this.submitButton = this.productForm?.querySelector('[type="submit"]') || this.form?.querySelector('[type="submit"]');
      this.submitLabel = this.submitButton?.querySelector('span');
      this.status = this.querySelector('[data-td-offer-status]');
      this.radios = Array.from(this.querySelectorAll('[data-offer-key]'));
      this.cards = Array.from(this.querySelectorAll('[data-td-offer-card]'));
      this.bundleCard = this.querySelector('[data-td-offer-card="bundle"]');
      this.bundleDetails = this.querySelector('[data-td-bundle-details]');
      this.benefitGroups = Array.from(this.querySelectorAll('[data-td-offer-benefits]'));
      this.soloGallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      this.bundleGallery = document.getElementById(`MediaGallery-${this.sectionId}-bundle`);
      this.productModal = document.getElementById(`ProductModal-${this.sectionId}`);
      this.productModalTemplate = document.getElementById(`ProductModalTemplate-${this.sectionId}`);
      this.sticky = this.querySelector('[data-td-offer-sticky]');
      this.stickyTitle = this.querySelector('[data-td-sticky-title]');
      this.stickyPrice = this.querySelector('[data-td-sticky-price]');
      this.stickyCyberPriceLabel = this.querySelector('[data-td-offer-sticky-cyber-price-label]');
      this.stickyButton = this.querySelector('[data-td-sticky-submit]');
      this.stickyButtonLabel = this.querySelector('[data-td-sticky-button-label]');
      this.selectedRadio = null;
      this.submitting = false;
      this.scrollTicking = false;
      this.viewTracked = false;
      this.bundleCardViewTracked = false;
      this.bundleDetailsTracked = false;

      if (!this.form || !this.variantInput || !this.submitButton || !this.radios.length) {
        this.dataset.initializationError = 'true';
        return;
      }

      this.onRadioChange = this.onRadioChange.bind(this);
      this.onStickyClick = this.onStickyClick.bind(this);
      this.onViewportActivity = this.onViewportActivity.bind(this);
      this.onProductFormStart = this.onProductFormStart.bind(this);
      this.onProductFormEnd = this.onProductFormEnd.bind(this);
      this.onProductFormError = this.onProductFormError.bind(this);
      this.onModalBuilt = this.onModalBuilt.bind(this);
      this.onBundleDetailsToggle = this.onBundleDetailsToggle.bind(this);

      this.radios.forEach((radio) => radio.addEventListener('change', this.onRadioChange));
      this.stickyButton?.addEventListener('click', this.onStickyClick);
      this.bundleDetails?.addEventListener('toggle', this.onBundleDetailsToggle);
      window.addEventListener('scroll', this.onViewportActivity, { passive: true });
      window.addEventListener('resize', this.onViewportActivity, { passive: true });
      document.addEventListener('td:product-form:submit-start', this.onProductFormStart);
      document.addEventListener('td:product-form:submit-end', this.onProductFormEnd);
      document.addEventListener('td:product-form:submit-error', this.onProductFormError);
      document.addEventListener('td:product-modal-built', this.onModalBuilt);

      const initiallyChecked = this.radios.find((radio) => radio.checked && !radio.disabled);
      const firstAvailable = this.radios.find((radio) => !radio.disabled && radio.dataset.available === 'true');
      const initialRadio = initiallyChecked || firstAvailable;

      if (initialRadio) {
        initialRadio.checked = true;
        this.applyOffer(initialRadio, { initial: true, announce: false });
      } else {
        this.disablePurchase(this.soldOutLabel);
      }

      this.trackSelectorView();
      this.trackBundleCardView();
      this.updateStickyVisibility();
    }

    disconnectedCallback() {
      this.radios?.forEach((radio) => radio.removeEventListener('change', this.onRadioChange));
      this.stickyButton?.removeEventListener('click', this.onStickyClick);
      this.bundleDetails?.removeEventListener('toggle', this.onBundleDetailsToggle);
      window.removeEventListener('scroll', this.onViewportActivity);
      window.removeEventListener('resize', this.onViewportActivity);
      document.removeEventListener('td:product-form:submit-start', this.onProductFormStart);
      document.removeEventListener('td:product-form:submit-end', this.onProductFormEnd);
      document.removeEventListener('td:product-form:submit-error', this.onProductFormError);
      document.removeEventListener('td:product-modal-built', this.onModalBuilt);
      this.viewObserver?.disconnect();
      this.bundleCardObserver?.disconnect();
    }

    onRadioChange(event) {
      const radio = event.currentTarget;
      if (!radio.checked || radio.disabled) return;
      if (radio.dataset.offerKey === 'bundle') this.preloadBundleHero();
      this.applyOffer(radio, { initial: false, announce: true });
    }

    applyOffer(radio, { initial = false, announce = true } = {}) {
      const offerKey = radio.dataset.offerKey;
      const available = radio.dataset.available === 'true' && Boolean(radio.dataset.variantId);

      if (!available) return;

      this.selectedRadio = radio;
      this.cards.forEach((card) => {
        const cardInput = card.querySelector('[data-offer-key]');
        const selected = cardInput === radio;
        card.classList.toggle('is-selected', selected);
        card.toggleAttribute('data-selected', selected);
      });

      this.variantInput.disabled = false;
      this.variantInput.value = radio.dataset.variantId;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));

      this.enablePurchase(this.getMainButtonText(radio));
      this.updateStickyContent(radio);
      this.switchGallery(offerKey);
      this.switchBenefits(offerKey);
      this.syncModalMedia(offerKey);

      if (announce && this.status) {
        this.status.textContent = `Seleccionaste ${radio.dataset.offerTitle}, ${radio.dataset.priceFormatted}.`;
      }

      const detail = this.getAnalyticsDetail(radio);
      document.dispatchEvent(new CustomEvent('td:offer-picker:change', { bubbles: true, detail }));

      if (!initial) {
        this.pushDataLayer('offer_option_clicked', detail);
      }

      this.updateStickyVisibility();
    }

    getMainButtonText(radio) {
      const label = radio.dataset.ctaLabel || 'Agregar al carrito';
      const price = radio.dataset.priceFormatted;
      return price ? `${label} — ${price}` : label;
    }

    switchBenefits(offerKey) {
      this.benefitGroups.forEach((group) => {
        const active = group.dataset.tdOfferBenefits === offerKey;
        group.hidden = !active;
        group.setAttribute('aria-hidden', String(!active));
        group.classList.toggle('is-active', active);
      });
    }

    enablePurchase(label) {
      this.submitButton.disabled = false;
      if (!this.submitting) this.submitButton.removeAttribute('aria-disabled');
      if (this.submitLabel) {
        this.submitLabel.textContent = label;
        this.submitLabel.classList.remove('hidden');
      }
      this.syncStickyDisabledState(false);
    }

    disablePurchase(label) {
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
      if (this.submitLabel) this.submitLabel.textContent = label;
      this.syncStickyDisabledState(true);
    }

    updateStickyContent(radio) {
      const price = Number(radio.dataset.price || 0);
      const comparePrice = Number(radio.dataset.comparePrice || 0);
      const hasVerifiedSavings = radio.dataset.available === 'true' &&
        Boolean(radio.dataset.variantId) && comparePrice > price;

      if (this.stickyTitle) this.stickyTitle.textContent = radio.dataset.offerTitle || radio.dataset.productTitle;
      if (this.stickyPrice) this.stickyPrice.textContent = radio.dataset.priceFormatted || '';
      if (this.stickyCyberPriceLabel) this.stickyCyberPriceLabel.hidden = !hasVerifiedSavings;
      if (this.stickyButtonLabel) this.stickyButtonLabel.textContent = radio.dataset.ctaLabel || 'Agregar';
      this.syncStickyDisabledState(radio.dataset.available !== 'true');
    }

    syncStickyDisabledState(disabled) {
      if (!this.stickyButton) return;
      this.stickyButton.disabled = disabled || this.submitting;
      this.stickyButton.setAttribute('aria-disabled', String(disabled || this.submitting));
    }

    switchGallery(offerKey) {
      const galleries = [
        { key: 'solo', element: this.soloGallery },
        { key: 'bundle', element: this.bundleGallery }
      ];

      if (offerKey === 'bundle' && !this.bundleGallery) return;

      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

      galleries.forEach(({ key, element }) => {
        if (!element) return;
        const active = key === offerKey;
        element.hidden = !active;
        element.setAttribute('aria-hidden', String(!active));
      });

      const activeGallery = offerKey === 'bundle' ? this.bundleGallery : this.soloGallery;
      if (!activeGallery) return;

      window.requestAnimationFrame(() => this.refreshGallery(activeGallery));
    }

    refreshGallery(gallery) {
      const firstMedia = gallery.querySelector('.product__media-item[data-media-id]');
      if (!firstMedia) return;

      if (typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(firstMedia.dataset.mediaId, false);
      } else {
        gallery.querySelectorAll('.product__media-item[data-media-id]').forEach((item) => {
          item.classList.toggle('is-active', item === firstMedia);
        });
      }

      const viewer = gallery.querySelector('slider-component[id^="GalleryViewer"]');
      const thumbnails = gallery.querySelector('slider-component[id^="GalleryThumbnails"]');

      if (typeof viewer?.resetPages === 'function') viewer.resetPages();
      if (typeof thumbnails?.resetPages === 'function') thumbnails.resetPages();

      if (viewer?.slider) viewer.slider.scrollTo({ left: 0, behavior: 'auto' });
      if (thumbnails?.slider) thumbnails.slider.scrollTo({ left: 0, behavior: 'auto' });
    }

    syncModalMedia(offerKey) {
      const syncChildren = (container) => {
        if (!container) return;
        Array.from(container.children).forEach((item) => {
          const group = item.dataset.tdOfferMedia;
          if (!group) return;
          item.hidden = group !== offerKey;
          item.classList.remove('active');
        });
      };

      if (this.productModalTemplate?.content) {
        syncChildren(this.productModalTemplate.content);
      }

      const modalContent = this.productModal?.querySelector('.product-media-modal__content');
      syncChildren(modalContent);

      if (typeof this.productModal?.refreshMedia === 'function') {
        this.productModal.refreshMedia();
      }
    }

    onModalBuilt(event) {
      if (String(event.detail?.sectionId) !== String(this.sectionId)) return;
      this.productModalTemplate = null;
      this.syncModalMedia(this.selectedRadio?.dataset.offerKey || 'solo');
    }

    onStickyClick() {
      if (!this.selectedRadio || this.submitting || this.stickyButton?.disabled) return;
      this.pushDataLayer('td_offer_sticky_add_click', this.getAnalyticsDetail(this.selectedRadio));

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(this.submitButton);
      } else {
        this.submitButton.click();
      }
    }

    onViewportActivity() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.updateStickyVisibility();
        this.scrollTicking = false;
      });
    }

    updateStickyVisibility() {
      if (!this.sticky) return;

      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const designMode = Boolean(window.Shopify?.designMode) || document.documentElement.classList.contains('shopify-design-mode');
      const buttonRect = this.submitButton?.getBoundingClientRect();
      const hasPassedPrimaryButton = Boolean(buttonRect && buttonRect.bottom < 0);
      const shouldShow = this.enableMobileSticky && mobile && !designMode && hasPassedPrimaryButton && Boolean(this.selectedRadio);

      this.sticky.classList.toggle('is-visible', shouldShow);
      this.sticky.setAttribute('aria-hidden', String(!shouldShow));
      this.sticky.toggleAttribute('inert', !shouldShow);
      if (this.stickyButton) this.stickyButton.tabIndex = shouldShow ? 0 : -1;
    }

    onProductFormStart(event) {
      if (event.detail?.formId !== this.form.id) return;
      this.submitting = true;
      this.sticky?.classList.add('is-loading');
      this.syncStickyDisabledState(false);

      if (this.selectedRadio) {
        this.pushDataLayer('td_offer_add_to_cart_click', this.getAnalyticsDetail(this.selectedRadio));
      }
    }

    onProductFormEnd(event) {
      if (event.detail?.formId !== this.form.id) return;
      this.submitting = false;
      this.sticky?.classList.remove('is-loading');
      this.syncStickyDisabledState(this.selectedRadio?.dataset.available !== 'true');
      this.updateStickyVisibility();
    }

    onProductFormError(event) {
      if (event.detail?.formId !== this.form.id) return;
      if (this.status) this.status.textContent = event.detail?.message || 'No pudimos agregar esta opción. Intenta nuevamente.';
    }

    onBundleDetailsToggle() {
      if (!this.bundleDetails?.open || this.bundleDetailsTracked) return;
      this.bundleDetailsTracked = true;
      const bundleRadio = this.radios.find((radio) => radio.dataset.offerKey === 'bundle');
      const detail = bundleRadio ? this.getAnalyticsDetail(bundleRadio) : {
        experiment_name: this.experimentName,
        section_id: this.sectionId,
        offer_key: 'bundle'
      };
      this.pushDataLayer('offer_details_opened', detail);
    }


    preloadBundleHero() {
      if (!this.bundleGallery || this.bundleGallery.dataset.tdHeroPreloaded === 'true') return;

      const firstImage = this.bundleGallery.querySelector('.product__media-item img');
      if (!firstImage) return;

      this.bundleGallery.dataset.tdHeroPreloaded = 'true';
      firstImage.loading = 'eager';
      firstImage.fetchPriority = 'low';
    }

    trackBundleCardView() {
      if (!this.bundleCard) return;
      const bundleRadio = this.radios.find((radio) => radio.dataset.offerKey === 'bundle');

      const track = () => {
        if (this.bundleCardViewTracked) return;
        this.bundleCardViewTracked = true;
        this.preloadBundleHero();
        const detail = bundleRadio ? this.getAnalyticsDetail(bundleRadio) : {
          experiment_name: this.experimentName,
          section_id: this.sectionId,
          offer_key: 'bundle'
        };
        this.pushDataLayer('kit_card_viewed', detail);
      };

      if (typeof window.IntersectionObserver !== 'function') {
        track();
        return;
      }

      this.bundleCardObserver = new window.IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
          track();
          this.bundleCardObserver.disconnect();
        }
      }, { threshold: [0.5] });

      this.bundleCardObserver.observe(this.bundleCard);
    }

    trackSelectorView() {
      const track = () => {
        if (this.viewTracked) return;
        this.viewTracked = true;
        const detail = this.selectedRadio ? this.getAnalyticsDetail(this.selectedRadio) : {
          experiment_name: this.experimentName,
          section_id: this.sectionId
        };
        this.pushDataLayer('offer_selector_view', detail);
      };

      if (typeof window.IntersectionObserver !== 'function') {
        track();
        return;
      }

      this.viewObserver = new window.IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
          track();
          this.viewObserver.disconnect();
        }
      }, { threshold: [0.5] });

      this.viewObserver.observe(this);
    }

    getAnalyticsDetail(radio) {
      return {
        experiment_name: this.experimentName,
        section_id: this.sectionId,
        offer_key: radio.dataset.offerKey,
        offer_title: radio.dataset.offerTitle,
        product_id: radio.dataset.productId,
        product_title: radio.dataset.productTitle,
        variant_id: radio.dataset.variantId,
        price: Number(radio.dataset.price || 0) / 100,
        currency: window.Shopify?.currency?.active || undefined
      };
    }

    pushDataLayer(eventName, detail = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...detail });
    }
  }

  customElements.define('td-offer-picker', TDOfferPicker);
})();